import styled from "styled-components";
import type { SxProps, Theme } from "@mui/material/styles";

const NEUTRAL_COLOR = "#91959B";
const AVAILABLE_COLOR = "#36D133";
const BUSY_COLOR = "#F4C933";

const BORDER_MUTED = "#CAC4D0";
const TEXT_PRIMARY = "#202224";

export const dividerSx: SxProps<Theme> = {
  borderColor: BORDER_MUTED,
  margin: "10px 0",
};

export const autocompleteRootSx: SxProps<Theme> = {
  width: "55%",
};

export const driverFieldColumnWidthSx: SxProps<Theme> = {
  width: "55%",
  minWidth: 200,
  maxWidth: 315,
};

export const autocompleteFieldLegendSx: SxProps<Theme> = {
  fontSize: 12,
  lineHeight: 1.43,
  fontWeight: 400,
  color: TEXT_PRIMARY,
  textTransform: "capitalize !important",
  mb: "4px",
};

export const autocompleteTextFieldSx: SxProps<Theme> = {
  minWidth: 200,
  maxWidth: 315,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    paddingLeft: "16px",
    minHeight: 56,
    height: 56,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-input": {
    paddingTop: 16,
    paddingBottom: 16,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: TEXT_PRIMARY,
  },
};

export const autocompletePaperSx: SxProps<Theme> = {
  minWidth: 315,
  borderRadius: "8px",
  marginTop: "4px",
};

export const autocompletePaperProps = {
  sx: autocompletePaperSx,
};

export const autocompleteListboxSx: SxProps<Theme> = {
  padding: 0,
  "& .MuiAutocomplete-option": {
    "&[aria-selected='true']": {
      backgroundColor: "transparent",
    },
    "&[aria-selected='true']:hover": {
      backgroundColor: "transparent",
    },
    "&[aria-selected='true'].Mui-focused": {
      backgroundColor: "transparent",
    },
    "&[aria-selected='true'].Mui-focusVisible": {
      backgroundColor: "transparent",
    },
  },
};

export const searchIconSx: SxProps<Theme> = {
  color: TEXT_PRIMARY,
  fontSize: 24,
  marginLeft: "8px",
};

export type StatusDotVariant = "neutral" | "available" | "busy";

interface StatusDotProps {
  variant: StatusDotVariant;
}

const resolveDotColor = (variant: StatusDotVariant): string => {
  if (variant === "neutral") return NEUTRAL_COLOR;
  if (variant === "busy") return BUSY_COLOR;
  return AVAILABLE_COLOR;
};

export const StatusDot = styled.span<StatusDotProps>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ variant }) => resolveDotColor(variant)};
`;

export const OptionRow = styled.div<{ $hasSubtitle?: boolean }>`
  display: flex;
  align-items: ${({ $hasSubtitle }) =>
    $hasSubtitle ? "flex-start" : "center"};
  gap: 16px;
  width: 100%;
  min-height: ${({ $hasSubtitle }) => ($hasSubtitle ? "56px" : "40px")};
  padding: ${({ $hasSubtitle }) =>
    $hasSubtitle ? "8px 24px 8px 16px" : "0 24px 0 16px"};
  box-sizing: border-box;
`;

export const OptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const OptionLabel = styled.span`
  font-size: 16px;
  line-height: 24px;
  color: #202224;
  font-weight: 400;
`;

export const OptionSubLabel = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: #91959b;
  font-weight: 400;
`;

export const StatusDotWrap = styled.span<{ $hasSubtitle?: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  margin-top: ${({ $hasSubtitle }) => ($hasSubtitle ? "4px" : "0")};
`;

export const GroupHeader = styled.div`
  padding: 10px 16px 0 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #202224;
`;

export const OptionsList = styled.ul`
  padding: 0;
  margin: 0;
  list-style: none;
  width: 100%;
`;

export const AutocompleteOptionLi = styled.li`
  padding: 0;
`;
